import { AlertCircle, Phone, ArrowLeft, CheckCircle, Sparkles, ShoppingBag, BarChart3 } from 'lucide-react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { GOOGLE_CLIENT_ID, API, apiPost } from '@/App';
import { auth, RecaptchaVerifier, signInWithPhoneNumber } from '@/lib/firebase';

const trackEvent = (eventType, element) => {
  fetch(`${API}/analytics/page-event`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event_type: eventType, page: 'login', element, referrer: document.referrer })
  }).catch(() => {});
};

export default function LoginPage() {
  const [isSafari, setIsSafari] = useState(false);
  const [authMode, setAuthMode] = useState('main'); // main, phone, otp
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recaptchaSolved, setRecaptchaSolved] = useState(false);
  const [productCount, setProductCount] = useState(null);
  const otpRefs = useRef([]);
  const recaptchaInitialized = useRef(false);

  useEffect(() => {
    const ua = navigator.userAgent;
    setIsSafari(/Safari/.test(ua) && !/Chrome/.test(ua) && !/CriOS/.test(ua));
    trackEvent('page_view', null);
    fetch(`${API}/health`).then(r => r.json()).then(d => setProductCount(d.products)).catch(() => {});
    // Clear any stale link-mode flag from a previous abandoned "Link Google" attempt on Settings.
    // Without this, the OAuth callback would route to /auth/google/link (which requires a logged-in
    // session) and return 401 "Unauthorized" — making it impossible to log in.
    localStorage.removeItem('google_link_mode');
  }, []);

  // Initialize visible reCAPTCHA when entering phone mode
  useEffect(() => {
    if (authMode !== 'phone') {
      recaptchaInitialized.current = false;
      setRecaptchaSolved(false);
      return;
    }

    const timer = setTimeout(() => {
      if (recaptchaInitialized.current) return;
      recaptchaInitialized.current = true;

      if (window.__gloverseRecaptcha) {
        try { window.__gloverseRecaptcha.clear(); } catch (e) { console.error('reCAPTCHA clear error:', e); }
        window.__gloverseRecaptcha = null;
      }

      const container = document.getElementById('recaptcha-container');
      if (container) { while (container.firstChild) container.removeChild(container.firstChild); }

      try {
        const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          size: 'normal',
          callback: () => {
            setRecaptchaSolved(true);
            setError('');
          },
          'expired-callback': () => {
            setRecaptchaSolved(false);
          }
        });
        window.__gloverseRecaptcha = verifier;
        verifier.render().catch(err => console.error('reCAPTCHA render error:', err));
      } catch (e) {
        console.error('reCAPTCHA init error:', e);
        setError('Failed to load verification. Please refresh the page.');
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [authMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (window.__gloverseRecaptcha) {
        try { window.__gloverseRecaptcha.clear(); } catch (e) { console.error('reCAPTCHA clear error:', e); }
        window.__gloverseRecaptcha = null;
      }
    };
  }, []);

  const handleGoogleLogin = () => {
    trackEvent('click', 'google_login_btn');
    // Use Emergent-managed Google Auth (works on web, PWA, future native app — no Google Cloud Console config needed).
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    handleEmergentLogin();
  };

  const handleEmergentLogin = () => {
    trackEvent('click', 'alternate_login_btn');
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSendOTP = async () => {
    trackEvent('click', 'send_otp_btn');
    setLoading(true);
    setError('');
    try {
      const verifier = window.__gloverseRecaptcha;
      if (!verifier) throw new Error('Please complete the verification checkbox first');
      const fullPhone = `${countryCode}${phoneNumber.replace(/\D/g, '')}`;
      const confirmationResult = await signInWithPhoneNumber(auth, fullPhone, verifier);
      window.__gloverseConfirmation = confirmationResult;
      setAuthMode('otp');
    } catch (err) {
      console.error('Send OTP error:', err);
      if (err.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else if (err.code === 'auth/invalid-phone-number') {
        setError('Invalid phone number. Please check and try again.');
      } else {
        setError(err.message || 'Failed to send OTP. Please try again.');
      }
      setRecaptchaSolved(false);
      recaptchaInitialized.current = false;
      if (window.grecaptcha) {
        try { window.grecaptcha.reset(); } catch (e) { console.error('grecaptcha reset error:', e); }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    trackEvent('click', 'verify_otp_btn');
    setLoading(true);
    setError('');
    try {
      const code = otp.join('');
      const result = await window.__gloverseConfirmation.confirm(code);
      const idToken = await result.user.getIdToken();
      const response = await apiPost(`${API}/auth/phone/verify`, { id_token: idToken });
      if (response.ok && response.data?.session_token) {
        localStorage.setItem('gloverse_token', response.data.session_token);
        window.location.href = '/';
      } else {
        setError(response.data?.detail || 'Verification failed. Please try again.');
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      if (err.code === 'auth/invalid-verification-code') {
        setError('Invalid OTP code. Please check and try again.');
      } else if (err.code === 'auth/code-expired') {
        setError('OTP expired. Please request a new one.');
      } else if (err.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(err.message || 'Verification failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = useCallback((e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      otpRefs.current[5]?.focus();
      e.preventDefault();
    }
  }, []);

  const VALUE_PROPS = [
    { icon: CheckCircle, text: "Attach product checklists to every post" },
    { icon: ShoppingBag, text: productCount ? `${productCount.toLocaleString()}+ products from top beauty brands` : "Thousands of products from top beauty brands" },
    { icon: Sparkles, text: "Get discovered on Spotlight" },
    { icon: BarChart3, text: "Creator dashboard to track your growth" },
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(135deg, hsl(330, 85%, 65%) 0%, hsl(25, 95%, 65%) 40%, hsl(150, 60%, 45%) 100%)' }}>
      {/* Urgency Banner */}
      <div className="w-full bg-black/80 backdrop-blur-sm text-center py-2 px-4" data-testid="urgency-banner">
        <p className="text-white/95 text-xs sm:text-sm font-medium tracking-wide" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Creator earnings beta rolling out soon
        </p>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Hero */}
          <div className="text-center mb-5 fade-in">
            <div className="flex items-center justify-center">
              <img src="/gloverse-logo.png" alt="Gloverse Logo" className="w-72 object-contain" />
            </div>
            <p className="text-white font-semibold text-base mt-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              The beauty platform where routines become revenue
            </p>
          </div>

          <div className="glass-card rounded-[2rem] p-8 shadow-2xl fade-in" style={{ animationDelay: '0.2s' }}>
            {/* Main Login Options */}
            {authMode === 'main' && (
              <div className="space-y-4">
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-foreground mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Join the community
                  </h2>
                  <p className="text-sm text-muted-foreground" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    Sign up in 10 seconds. No credit card needed.
                  </p>
                </div>

                <button
                  data-testid="google-login-btn"
                  onClick={handleGoogleLogin}
                  className="w-full btn-primary flex items-center justify-center gap-3 text-lg"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </button>

                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground uppercase">or</span>
                  <div className="flex-1 h-px bg-border" />
                </div>

                <button
                  data-testid="phone-login-btn"
                  onClick={() => { trackEvent('click', 'phone_login_btn'); setAuthMode('phone'); setError(''); }}
                  className="w-full flex items-center justify-center gap-3 py-3 px-6 rounded-xl border-2 border-border bg-white/80 hover:bg-white/90 transition-colors text-foreground font-medium"
                >
                  <Phone className="w-5 h-5" />
                  Continue with Phone
                </button>

                {/* Value Props */}
                <div className="pt-3 space-y-2.5">
                  {VALUE_PROPS.map((vp) => (
                    <div key={vp.text} className="flex items-center gap-2.5">
                      <vp.icon className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span className="text-xs text-muted-foreground">{vp.text}</span>
                    </div>
                  ))}
                </div>

                <div className="text-center pt-1">
                  <button
                    data-testid="alternate-login-btn"
                    onClick={handleEmergentLogin}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors underline"
                  >
                    Having trouble? Try alternate login
                  </button>
                </div>

                <div className="text-center text-xs text-muted-foreground">
                  By continuing, you agree to our{' '}
                  <a href="/terms" className="underline hover:text-primary">Terms</a> &{' '}
                  <a href="/privacy" className="underline hover:text-primary">Privacy Policy</a>
                </div>
              </div>
            )}

            {/* Phone Number Input */}
            {authMode === 'phone' && (
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <button onClick={() => { setAuthMode('main'); setError(''); }} className="p-1.5 hover:bg-gray-100 rounded-full" data-testid="phone-back-btn">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Enter your phone number
                  </h2>
                </div>

                <p className="text-sm text-muted-foreground">
                  We'll send you a one-time verification code via SMS.
                </p>

                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="w-24 px-3 py-3 rounded-xl border-2 border-border bg-white/80 text-sm font-medium focus:border-primary focus:ring-1 focus:ring-primary"
                    data-testid="country-code-select"
                  >
                    <option value="+91">+91</option>
                    <option value="+1">+1</option>
                    <option value="+44">+44</option>
                    <option value="+61">+61</option>
                    <option value="+971">+971</option>
                    <option value="+65">+65</option>
                    <option value="+60">+60</option>
                    <option value="+81">+81</option>
                    <option value="+82">+82</option>
                    <option value="+49">+49</option>
                    <option value="+33">+33</option>
                    <option value="+86">+86</option>
                    <option value="+7">+7</option>
                    <option value="+55">+55</option>
                    <option value="+966">+966</option>
                    <option value="+234">+234</option>
                    <option value="+254">+254</option>
                    <option value="+27">+27</option>
                    <option value="+62">+62</option>
                    <option value="+63">+63</option>
                  </select>
                  <input
                    data-testid="phone-number-input"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Phone number"
                    className="flex-1 px-4 py-3 rounded-xl border-2 border-border bg-white/80 text-lg focus:border-primary focus:ring-1 focus:ring-primary"
                    autoFocus
                  />
                </div>

                {/* Visible reCAPTCHA checkbox */}
                <div className="flex justify-center" id="recaptcha-container" data-testid="recaptcha-container" />

                {error && (
                  <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-xl">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    {error}
                  </div>
                )}

                <button
                  data-testid="send-otp-btn"
                  onClick={handleSendOTP}
                  disabled={loading || phoneNumber.replace(/\D/g, '').length < 8 || !recaptchaSolved}
                  className="w-full btn-primary py-3 text-lg disabled:opacity-50"
                >
                  {loading ? 'Sending OTP...' : 'Send OTP'}
                </button>

                <button
                  onClick={() => { setAuthMode('main'); setError(''); }}
                  className="w-full text-sm text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
              </div>
            )}

            {/* OTP Verification */}
            {authMode === 'otp' && (
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <button onClick={() => { setAuthMode('phone'); setOtp(['','','','','','']); setError(''); }} className="p-1.5 hover:bg-gray-100 rounded-full" data-testid="otp-back-btn">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Verify OTP
                  </h2>
                </div>

                <p className="text-sm text-muted-foreground">
                  Enter the 6-digit code sent to <strong>{countryCode} {phoneNumber}</strong>
                </p>

                <div className="flex gap-2 justify-center" onPaste={handleOtpPaste}>
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (otpRefs.current[i] = el)}
                      data-testid={`otp-input-${i}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      className="w-11 h-14 text-center text-xl font-bold rounded-xl border-2 border-border bg-white/80 focus:border-primary focus:ring-1 focus:ring-primary"
                      autoFocus={i === 0}
                    />
                  ))}
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-xl">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    {error}
                  </div>
                )}

                <button
                  data-testid="verify-otp-btn"
                  onClick={handleVerifyOTP}
                  disabled={loading || otp.join('').length !== 6}
                  className="w-full btn-primary py-3 text-lg disabled:opacity-50"
                >
                  {loading ? 'Verifying...' : 'Verify & Login'}
                </button>

                <button
                  onClick={() => { setOtp(['','','','','','']); handleSendOTP(); }}
                  disabled={loading}
                  className="w-full text-sm text-muted-foreground hover:text-primary transition-colors"
                  data-testid="resend-otp-btn"
                >
                  Didn't receive the code? Resend OTP
                </button>
              </div>
            )}
          </div>

          {/* Quick Start link */}
          <div className="text-center mt-4 fade-in" style={{ animationDelay: '0.4s' }}>
            <a href="/start" className="text-white/80 text-sm hover:text-white underline" data-testid="quickstart-link">
              New here? See how Gloverse works
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
